import React, { useState, useEffect } from 'react';

const SPIN_ANIMATIONS = [
  'spinoza-spin-left',
  'spinoza-spin-right',
  'spinoza-spin-polar',
  'spinoza-spin-equatorial',
];

/**
 * SpinozaAvatar — Inline SVG cartoon face with philosopher-style details.
 * CSS animation classes: spinoza-avatar, spinoza-eyes, spinoza-mouth.
 *
 * Props:
 *   state: 'idle' | 'listening' | 'speaking' | 'thinking'
 *   size: number (px, default 96)
 */
const SpinozaAvatar = ({ state = 'idle', isTyping = false, browCocked = false, size = 96 }) => {
  // Build class list: use isTyping (ground-truth from SpinozaBubble) for speaking mouth animation
  let stateClass = 'spinoza-avatar';
  if (isTyping) {
    stateClass += ' spinoza-avatar--speaking';
  } else if (state === 'listening') {
    stateClass += ' spinoza-avatar--listening';
  } else if (state === 'thinking') {
    stateClass += ' spinoza-avatar--thinking';
  }

  // Random spin animation at random intervals (8–15s)
  const [spin, setSpin] = useState(null);

  useEffect(() => {
    const scheduleNext = () => {
      const delay = 8000 + Math.random() * 7000; // 8–15s
      return setTimeout(() => {
        const anim = SPIN_ANIMATIONS[Math.floor(Math.random() * SPIN_ANIMATIONS.length)];
        setSpin(anim);
        // Clear after 1s
        const clearId = setTimeout(() => setSpin(null), 1000);
        // Store clear timeout for cleanup
        clearRef.current = clearId;
        // Schedule next spin
        intervalRef.current = scheduleNext();
      }, delay);
    };

    const clearRef = { current: null };
    const intervalRef = { current: scheduleNext() };

    return () => {
      clearTimeout(intervalRef.current);
      if (clearRef.current) clearTimeout(clearRef.current);
    };
  }, []);

  // Random sideways glance at random intervals (6–14s)
  const [glance, setGlance] = useState(0); // -2 = left, 0 = centre, 2 = right

  useEffect(() => {
    const scheduleGlance = () => {
      const delay = 6000 + Math.random() * 8000; // 6–14s
      return setTimeout(() => {
        const dir = Math.random() < 0.5 ? -2 : 2;
        setGlance(dir);
        // Hold the glance, then return to centre
        const returnId = setTimeout(() => setGlance(0), 600);
        glanceClearRef.current = returnId;
        glanceTimerRef.current = scheduleGlance();
      }, delay);
    };

    const glanceClearRef = { current: null };
    const glanceTimerRef = { current: scheduleGlance() };

    return () => {
      clearTimeout(glanceTimerRef.current);
      if (glanceClearRef.current) clearTimeout(glanceClearRef.current);
    };
  }, []);

  return (
    <div style={{ perspective: '200px' }}>
      <div
        className={stateClass}
        style={{
          width: size,
          height: size,
          animation: spin ? `${spin} 1s ease-in-out` : undefined,
        }}
      >
        <svg
          viewBox="0 0 96 96"
          width={size}
          height={size}
          xmlns="http://www.w3.org/2000/svg"
          aria-label="Spinoza AI Treasury Manager"
          role="img"
        >
          {/* Clip paths for eyelid blink over spectacle lenses */}
          <defs>
            <clipPath id="lens-l"><circle cx="36" cy="40" r="9.5" /></clipPath>
            <clipPath id="lens-r"><circle cx="60" cy="40" r="9.5" /></clipPath>
          </defs>

          {/* Round spectacles */}
          <circle cx="36" cy="40" r="10" fill="none" stroke="#000000" strokeWidth="1.8" />
          <circle cx="60" cy="40" r="10" fill="none" stroke="#000000" strokeWidth="1.8" />
          {/* Bridge */}
          <path d="M 46 40 Q 48 36 50 40" fill="none" stroke="#000000" strokeWidth="1.5" />
          {/* Temple arms */}
          <line x1="26" y1="39" x2="18" y2="36" stroke="#000000" strokeWidth="1.5" strokeLinecap="round" />
          <line x1="70" y1="39" x2="78" y2="36" stroke="#000000" strokeWidth="1.5" strokeLinecap="round" />

          {/* Eyes — outer <g> for glance offset, inner for CSS blink */}
          <g transform={glance ? `translate(${glance}, 0)` : undefined}>
            <g className="spinoza-eyes">
              <ellipse cx="36" cy="40" rx="3.5" ry="4" fill="#000000" />
              <ellipse cx="60" cy="40" rx="3.5" ry="4" fill="#000000" />
              {/* Eye highlights */}
              <circle cx="37.5" cy="38.5" r="1.5" fill="white" opacity="0.85" />
              <circle cx="61.5" cy="38.5" r="1.5" fill="white" opacity="0.85" />
            </g>
          </g>

          {/* Eyelids — scale from top over lenses, clipped to each spectacle */}
          <rect className="spinoza-eyelid" clipPath="url(#lens-l)" x="25" y="30" width="22" height="20" fill="#000000" />
          <rect className="spinoza-eyelid" clipPath="url(#lens-r)" x="49" y="30" width="22" height="20" fill="#000000" />

          {/* Eyebrows — grouped for shared raise, individual classes for quirk */}
          <g className="spinoza-brows">
            <path className="spinoza-brow-left" d="M 28 24 Q 36 20 44 24" fill="none" stroke="#000000" strokeWidth="1.2" strokeLinecap="round" />
            <path className="spinoza-brow-right" d={browCocked ? "M 52 24 Q 60 16 68 20" : "M 52 24 Q 60 20 68 24"} fill="none" stroke="#000000" strokeWidth="1.2" strokeLinecap="round" />
          </g>

          {/* Small nose */}
          <path d="M 48 44 L 46 52 Q 48 54 50 52 Z" fill="#3832e3" opacity="0.6" />

          {/* Gentle smile */}
          <path className="spinoza-mouth" d="M 40 60 Q 48 67 56 60" fill="none" stroke="#000000" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </div>
    </div>
  );
};

export default SpinozaAvatar;
